'use client'

import { Card, CardContent } from '@/components/ui/card'
import { Shield, FileCheck, Handshake, LifeBuoy, CheckCircle, LucideIcon } from 'lucide-react'

interface TrustCard {
  icon: LucideIcon
  title: string
  description: string
  bullets: string[]
}

const trustCards: TrustCard[] = [
  {
    icon: Shield,
    title: '검증 기준 공개',
    description: '팀 참여 전 체크하는 핵심 항목을 투명하게 관리합니다',
    bullets: ['포트폴리오/경력/레퍼런스 확인', '사업자·신원 정보 및 평판 검토', '검증 통과 팀만 요청 수신'],
  },
  {
    icon: FileCheck,
    title: '비교 가능한 견적 포맷',
    description: '기간·범위·산출물·인력 구성 항목을 맞춰 비교합니다',
    bullets: ['조건 통일된 비교 카드 제공', '견적별 상세 제안서·대화 이력 확인', '의사결정에 필요한 핵심 정보 요약'],
  },
  {
    icon: Handshake,
    title: '진행 안전장치',
    description: '선택 이후 단계에서도 불안을 줄이는 지원 체계를 제공합니다',
    bullets: ['계약 템플릿/NDA 가이드 제공', '진행 중 이슈 발생 시 분쟁 대응 안내', '원할 때만 미팅·계약 단계로 전환'],
  },
  {
    icon: LifeBuoy,
    title: '운영·응답 관리',
    description: '요청 이후 흐름을 놓치지 않도록 운영 지표를 관리합니다',
    bullets: ['평균 첫 응답 시간 모니터링', '요청 상태별 알림·리마인드 제공', '팀 매칭 품질 지표 지속 개선'],
  },
]

const neutralData = [
  {
    title: '등록 팀 수',
    value: '420+',
    description: '검증 기준을 통과한 팀 기준 (2026년 2월)',
  },
  {
    title: '평균 첫 견적 도착 시간',
    value: '6시간',
    description: '최근 30일 요청 데이터 기준 평균값',
  },
  {
    title: '최근 30일 매칭 완료 수',
    value: '168건',
    description: '요청 후 팀 선택까지 완료된 건수',
  },
]

interface ClientTrustAndTestimonialsProps {
  isVisible: boolean
}

export default function ClientTrustAndTestimonials({ isVisible }: ClientTrustAndTestimonialsProps) {
  return (
    <section
      className={`w-full bg-white px-4 py-16 sm:px-6 md:py-20 lg:px-8 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-16">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">검증 근거와 보호 장치</h2>
            <p className="mx-auto max-w-2xl text-sm text-gray-600 md:text-base">
              기능 소개보다, 실제로 무엇을 확인하고 어떻게 보호하는지 먼저 보여드립니다.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {trustCards.map((card, index) => {
              const Icon = card.icon

              return (
                <Card
                  key={card.title}
                  className={`flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:border-blue-200 hover:shadow-md min-h-[300px] ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{
                    transitionDelay: isVisible ? `${index * 100}ms` : '0ms',
                  }}
                >
                  <CardContent className="flex flex-col flex-1 p-6">
                    <div className="mb-4 flex justify-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
                        <Icon className="h-6 w-6 text-[#06f]" />
                      </div>
                    </div>

                    <h3 className="mb-2 text-lg font-semibold text-gray-900 text-center">{card.title}</h3>
                    <p className="text-sm font-medium text-gray-700 mb-4 text-center">{card.description}</p>

                    <div className="flex-1 space-y-2">
                      {card.bullets.map((bullet, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                          <CheckCircle className="h-3.5 w-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <span>{bullet}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>

        <div>
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">서비스 현황 지표</h2>
            <p className="mx-auto max-w-2xl text-base text-gray-600 md:text-lg">최근 운영 데이터를 숫자로 확인하세요</p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {neutralData.map((item, index) => (
              <Card
                key={item.title}
                className={`rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:shadow-md ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{
                  transitionDelay: isVisible ? `${(trustCards.length + index) * 100}ms` : '0ms',
                }}
              >
                <CardContent className="p-6">
                  <p className="text-xs font-semibold text-blue-600">{item.title}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{item.value}</p>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
